import {
  useProduct,
  useSelectedVariant,
  useSelectionActions
} from '@/components/product/product-context';

/** The value `variant` carries for the option named `name`. */
const optionValueOf = (variant, name) =>
  variant.selectedOptions.find((o) => o.name === name)?.value;

/**
 * Everything the swatch grids need from the selected variant: the value that is
 * currently picked, per-swatch stock, and the click handler.
 *
 * Reads the selected variant and the selection actions, so it can only be
 * called inside a Suspense boundary. Both grids take the whole object as a
 * single `selection` prop, and a grid rendered without it is the static
 * fallback.
 */
export function useSwatchSelection(optionName) {
  const { productData } = useProduct();
  const { variant: currentVariant } = useSelectedVariant();
  const { updateOption } = useSelectionActions();
  const { options, variants } = productData.shopifyProduct;

  const currentValue = optionValueOf(currentVariant, optionName);

  // Picking a swatch only changes this one option, so every other option keeps
  // the value it has now. Those are what decide which variant a swatch lands on.
  const otherOptions = currentVariant.selectedOptions.filter(
    (o) => o.name !== optionName
  );
  const keepsCurrentSelection = (variant) =>
    otherOptions.every((o) => optionValueOf(variant, o.name) === o.value);

  // One pass over the variants for the whole grid, rather than a scan per
  // swatch: a swatch is unavailable when the variant it would select is.
  const outOfStockValues = new Set(
    variants
      .filter((variant) => variant.outOfStock && keepsCurrentSelection(variant))
      .map((variant) => optionValueOf(variant, optionName))
  );

  const isOutOfStock = (swatchValue) => outOfStockValues.has(swatchValue);

  const selectOption = (swatchValue) => {
    const matchedName = options.find((option) =>
      option.values.includes(swatchValue)
    )?.name;

    if (!matchedName) {
      console.error('No option name matched for', swatchValue);
      return;
    }

    updateOption({ [matchedName.toLowerCase()]: swatchValue });
  };

  return { currentValue, isOutOfStock, selectOption };
}
